import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { fetchBoards, createBoard, addBoardItem } from '../services/dashboardService';
import '../styles/Whiteboard.css';

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const PinToBoardContext = createContext(null);

/**
 * usePinToBoard()
 * Returns { pinItem }
 * Call pinItem(item, anchorEl) from any component to open the board picker.
 *
 * item shape:
 *   {
 *     item_type: 'intelligence' | 'knowledge_node' | 'pdf' | 'news' | 'market_data' | 'custom',
 *     ref_id:    String,    // the source record's primary key
 *     title:     String,
 *     content?:  String,
 *     metadata?: Object,
 *     tags?:     String[],
 *   }
 */
export const usePinToBoard = () => {
  const ctx = useContext(PinToBoardContext);
  if (!ctx) throw new Error('usePinToBoard must be used within PinToBoardProvider');
  return ctx;
};

// ─────────────────────────────────────────────────────────────────────────────
// BoardPickerPopover — floating popover anchored near the trigger button
// ─────────────────────────────────────────────────────────────────────────────

const BoardPickerPopover = ({ item, anchorRect, boards, onClose, onBoardsUpdate }) => {
  const [pinning, setPinning]         = useState(null);    // boardId currently being pinned
  const [creating, setCreating]       = useState(false);
  const [showNewInput, setShowNewInput] = useState(false);
  const [newName, setNewName]         = useState('');
  const [feedback, setFeedback]       = useState(null);    // { text, type }
  const popRef     = useRef(null);
  const newInputRef = useRef(null);

  // ── Position ────────────────────────────────────────────────────────────────
  const [pos, setPos] = useState(() => {
    if (!anchorRect) return { top: 120, left: 120 };
    return { top: anchorRect.bottom + 8, left: anchorRect.left };
  });

  // After first paint, clamp to viewport so the popover never clips off-screen
  useEffect(() => {
    if (!popRef.current) return;
    const pop = popRef.current.getBoundingClientRect();
    const vw  = window.innerWidth;
    const vh  = window.innerHeight;
    let { top, left } = pos;

    if (left + pop.width > vw - 8) left = Math.max(8, vw - pop.width - 8);
    if (top + pop.height > vh - 8 && anchorRect) top = Math.max(8, anchorRect.top - pop.height - 8);

    setPos({ top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount

  // ── Close on outside click ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // ── Close on Escape ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Auto-focus new board input ──────────────────────────────────────────────
  useEffect(() => {
    if (showNewInput && newInputRef.current) newInputRef.current.focus();
  }, [showNewInput]);

  // ── Pin to an existing board ────────────────────────────────────────────────
  const handlePin = useCallback(async (boardId, boardName) => {
    if (pinning) return;
    setPinning(boardId);
    setFeedback(null);

    const res = await addBoardItem(boardId, item);
    if (res.success) {
      setFeedback({ text: `Pinned to "${boardName}" ✓`, type: 'success' });
      onBoardsUpdate(boardId, null);
      setTimeout(onClose, 1400);
    } else {
      const isDuplicate =
        res.message?.toLowerCase().includes('already') ||
        res.message?.toLowerCase().includes('duplicate') ||
        res.message?.toLowerCase().includes('unique') ||
        String(res.status) === '409';
      setFeedback({
        text: isDuplicate ? `Already on "${boardName}"` : (res.message || 'Failed to pin'),
        type: isDuplicate ? 'warn' : 'error',
      });
      setPinning(null);
    }
  }, [item, pinning, onClose, onBoardsUpdate]);

  // ── Create new board then pin ───────────────────────────────────────────────
  const handleCreateAndPin = useCallback(async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setFeedback(null);

    const createRes = await createBoard({ name });
    if (!createRes.success) {
      setFeedback({ text: createRes.message || 'Failed to create board', type: 'error' });
      setCreating(false);
      return;
    }
    const newBoard = createRes.data;
    onBoardsUpdate(null, newBoard);

    const pinRes = await addBoardItem(newBoard.id, item);
    if (pinRes.success) {
      setFeedback({ text: `Board created & item pinned ✓`, type: 'success' });
      onBoardsUpdate(newBoard.id, null);
      setTimeout(onClose, 1400);
    } else {
      setFeedback({ text: pinRes.message || 'Board created but pin failed', type: 'error' });
    }
    setCreating(false);
  }, [newName, creating, item, onClose, onBoardsUpdate]);

  const shortTitle = item.title
    ? item.title.length > 44 ? item.title.slice(0, 42) + '…' : item.title
    : 'Item';

  return (
    <div
      ref={popRef}
      className="wb-pin-popover"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
    >
      {/* Header */}
      <div className="wb-pin-popover-header">
        <i className="fas fa-thumbtack wb-pin-icon" />
        <span className="wb-pin-popover-label" title={item.title}>{shortTitle}</span>
        <button className="wb-pin-popover-close" onClick={onClose} title="Close">
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`wb-pin-feedback wb-pin-feedback-${feedback.type}`}>
          {feedback.text}
        </div>
      )}

      {/* Board list */}
      <div className="wb-pin-board-list">
        {boards.length === 0 ? (
          <p className="wb-pin-no-boards">No boards yet — create one below</p>
        ) : (
          boards.map((b) => (
            <button
              key={b.id}
              className="wb-pin-board-row"
              onClick={() => handlePin(b.id, b.name)}
              disabled={!!pinning || creating}
            >
              <span
                className="wb-pin-board-dot"
                style={{ background: b.color || '#94a3b8' }}
              />
              <span className="wb-pin-board-name">{b.name}</span>
              <span className="wb-pin-board-count">{b.item_count ?? 0}</span>
              {pinning === b.id && (
                <i className="fas fa-circle-notch fa-spin wb-pin-board-spinner" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer — create new board */}
      <div className="wb-pin-popover-footer">
        {showNewInput ? (
          <div className="wb-pin-new-row">
            <input
              ref={newInputRef}
              className="wb-pin-new-input"
              placeholder="Board name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateAndPin();
                if (e.key === 'Escape') { setShowNewInput(false); setNewName(''); }
              }}
              disabled={creating}
            />
            <button
              className="wb-pin-new-confirm"
              onClick={handleCreateAndPin}
              disabled={creating || !newName.trim()}
            >
              {creating
                ? <i className="fas fa-circle-notch fa-spin" />
                : 'Create & Pin'
              }
            </button>
          </div>
        ) : (
          <button className="wb-pin-new-btn" onClick={() => setShowNewInput(true)}>
            <i className="fas fa-plus" /> New board…
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export const PinToBoardProvider = ({ children }) => {
  const [boards, setBoards] = useState([]);
  const [modal, setModal]   = useState(null); // { item, anchorRect }
  const loadedRef = useRef(false);

  // Lazy-load board list on first pin action
  const loadBoards = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const res = await fetchBoards();
    if (res.success) setBoards(res.data || []);
  }, []);

  /**
   * pinItem(item, anchorEl)
   *   item     — descriptor object (see usePinToBoard jsdoc above)
   *   anchorEl — the DOM element to anchor the popover near (e.g. the pin button)
   */
  const pinItem = useCallback((item, anchorEl) => {
    const rect = anchorEl?.getBoundingClientRect?.() ?? null;
    loadBoards();
    setModal({ item, anchorRect: rect });
  }, [loadBoards]);

  // Update board list state after a pin or a new-board creation
  const handleBoardsUpdate = useCallback((pinnedBoardId, newBoard) => {
    setBoards((prev) => {
      let next = prev;
      if (newBoard)        next = [...next, newBoard];
      if (pinnedBoardId)   next = next.map((b) =>
        b.id === pinnedBoardId
          ? { ...b, item_count: (b.item_count || 0) + 1 }
          : b,
      );
      return next;
    });
  }, []);

  /**
   * removeBoard(boardId)
   * Call this from WhiteboardPage (or any component) after successfully
   * deleting a board so the pin popover board list stays in sync.
   */
  const removeBoard = useCallback((boardId) => {
    setBoards((prev) => prev.filter((b) => b.id !== boardId));
  }, []);

  const handleClose = useCallback(() => setModal(null), []);

  return (
    <PinToBoardContext.Provider value={{ pinItem, boards, removeBoard }}>
      {children}
      {modal && (
        <BoardPickerPopover
          item={modal.item}
          anchorRect={modal.anchorRect}
          boards={boards}
          onClose={handleClose}
          onBoardsUpdate={handleBoardsUpdate}
        />
      )}
    </PinToBoardContext.Provider>
  );
};
